export type ColorVisionMode = 'standard' | 'deuteranopia' | 'protanopia' | 'tritanopia';
export type TooltipDelay = 'instant' | 'short' | 'relaxed';

export interface PlayerPreferencesState {
  textScale: number;
  uiScale: number;
  highContrast: boolean;
  colorVision: ColorVisionMode;
  reducedMotion: boolean;
  screenShake: boolean;
  subtitles: boolean;
  edgePan: boolean;
  cameraSpeed: number;
  tooltipDelay: TooltipDelay;
  confirmEndTurn: boolean;
  autoPauseEvents: boolean;
  tutorialEnabled: boolean;
  contextualHints: boolean;
  masterVolume: number;
  musicVolume: number;
  sfxVolume: number;
  muted: boolean;
}

const STORAGE_KEY = 'conquest-of-java:player-preferences:v1';
const DEFAULTS: PlayerPreferencesState = {
  textScale: 1,
  uiScale: 1,
  highContrast: false,
  colorVision: 'standard',
  reducedMotion: false,
  screenShake: true,
  subtitles: true,
  edgePan: true,
  cameraSpeed: 1,
  tooltipDelay: 'short',
  confirmEndTurn: true,
  autoPauseEvents: true,
  tutorialEnabled: true,
  contextualHints: true,
  masterVolume: 1,
  musicVolume: 0.55,
  sfxVolume: 0.7,
  muted: false,
};

export class PlayerPreferences {
  static readonly instance = new PlayerPreferences();
  private state: PlayerPreferencesState = { ...DEFAULTS };

  private constructor() {
    this.load();
  }

  get value(): Readonly<PlayerPreferencesState> {
    return this.state;
  }

  update(patch: Partial<PlayerPreferencesState>) {
    this.state = {
      ...this.state,
      ...patch,
      textScale: this.clamp(patch.textScale ?? this.state.textScale, 0.85, 1.4),
      uiScale: this.clamp(patch.uiScale ?? this.state.uiScale, 0.9, 1.2),
      cameraSpeed: this.clamp(patch.cameraSpeed ?? this.state.cameraSpeed, 0.6, 1.8),
      masterVolume: this.clamp(patch.masterVolume ?? this.state.masterVolume, 0, 1),
      musicVolume: this.clamp(patch.musicVolume ?? this.state.musicVolume, 0, 1),
      sfxVolume: this.clamp(patch.sfxVolume ?? this.state.sfxVolume, 0, 1),
    };
    this.save();
    if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('coj:preferences-changed', { detail: this.state }));
  }

  reset() {
    this.state = { ...DEFAULTS };
    this.save();
  }

  fontSize(base: number) {
    return Math.max(11, Math.round(base * this.state.textScale));
  }

  tweenDuration(base: number) {
    return this.state.reducedMotion ? 0 : base;
  }

  tooltipDelayMs() {
    return this.state.tooltipDelay === 'instant' ? 0 : this.state.tooltipDelay === 'short' ? 240 : 650;
  }

  semanticColor(kind: 'success' | 'warning' | 'danger' | 'information') {
    if (this.state.highContrast) {
      return { success: 0x7dffb2, warning: 0xffe07d, danger: 0xff8d87, information: 0x8ed8ff }[kind];
    }
    const palettes: Record<ColorVisionMode, Record<typeof kind, number>> = {
      standard: { success: 0x69a982, warning: 0xd8b45a, danger: 0xd66b68, information: 0x4f9fbc },
      deuteranopia: { success: 0x56b4e9, warning: 0xf0e442, danger: 0xd55e00, information: 0x7aa6ff },
      protanopia: { success: 0x56b4e9, warning: 0xf0e442, danger: 0xcc79a7, information: 0x7aa6ff },
      tritanopia: { success: 0x4daf8a, warning: 0xffb000, danger: 0xe24a33, information: 0xb07aa1 },
    };
    return palettes[this.state.colorVision][kind];
  }

  private load() {
    if (typeof localStorage === 'undefined') return;
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as Partial<PlayerPreferencesState> | null;
      if (saved) this.state = { ...DEFAULTS, ...saved };
    } catch {
      this.state = { ...DEFAULTS };
    }
  }

  private save() {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
  }

  private clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
  }
}
